/**
 * Source Node Component for React Flow
 *
 * Renders inflow sources on the boundary edge.
 * Green styling to indicate value entering the system.
 */
import { memo, useState } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import type { ValueFlowNodeData } from "../../../../utils/schemaToReactFlow";
import { useHoverContext } from "../HoverContext";
import { NodeHoverLabel } from "./NodeHoverLabel";
import { NodeMetadataTooltip } from "./NodeMetadataTooltip";
import { ObservableBadgesContainer } from "./ObservableBadge";

/**
 * Resolve spec_icon path to actual URL
 */
const resolveSpecIconUrl = (specIcon: string): string => {
  const filename = specIcon.split("/").pop();
  return `/assets/sa-elements/${filename}`;
};

/**
 * Default inflow icon SVG
 */
const InflowIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12l7 7 7-7" />
  </svg>
);

export const SourceNodeRF = memo(({ id, data, selected }: NodeProps<ValueFlowNodeData>) => {
  const [imageError, setImageError] = useState(false);
  const { hoveredNodeId } = useHoverContext();
  const isHovered = hoveredNodeId === id;
  const isMinimal = data.display === "minimal";
  const isHidden = isMinimal && data.label.trim().length === 0 && !data.icon?.spec_icon;

  if (isMinimal) {
    // Standard nodes are 80px (2/3 of core's 120px)
    const scale = data.showLabel === false ? Math.min(data.scale ?? 1, 1) : 1;
    const iconSize = Math.max(32, Math.round(80 * scale));
    const fallbackSize = Math.max(24, Math.round(54 * scale));
    const wrapperClass =
      data.showLabel === false
        ? "flex items-center justify-center relative"
        : "flex flex-col items-center gap-2 relative";
    const hiddenHandleClass = "!opacity-0 !w-2 !h-2 !bg-emerald-300";
    return (
      <div
        data-testid={`value-flow-node-source-${id}`}
        className={isHidden ? "opacity-0 w-2 h-2" : wrapperClass}
        style={
          !isHidden && data.showLabel === false ? { width: iconSize, height: iconSize } : undefined
        }
      >
        {/* Hover components - only show if not hidden */}
        {!isHidden && (
          <>
            <NodeHoverLabel name={data.label} description={data.description} visible={isHovered} />
            <NodeMetadataTooltip
              originalData={data.originalData}
              nodeCategory="source"
              isHovered={isHovered}
            />
          </>
        )}
        {/* All 4 target handles */}
        <Handle type="target" position={Position.Top} id="top" className={hiddenHandleClass} />
        <Handle
          type="target"
          position={Position.Right}
          id="right-in"
          className={hiddenHandleClass}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom-in"
          className={hiddenHandleClass}
        />
        <Handle type="target" position={Position.Left} id="left-in" className={hiddenHandleClass} />
        {/* All 4 source handles */}
        <Handle type="source" position={Position.Top} id="top-out" className={hiddenHandleClass} />
        <Handle type="source" position={Position.Right} id="right" className={hiddenHandleClass} />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className={hiddenHandleClass}
        />
        <Handle type="source" position={Position.Left} id="left" className={hiddenHandleClass} />

        {!isHidden && (
          <>
            <div className="relative flex items-center justify-center">
              {data.icon?.spec_icon && !imageError ? (
                <img
                  src={resolveSpecIconUrl(data.icon.spec_icon)}
                  alt=""
                  className="object-contain"
                  style={{ width: iconSize, height: iconSize }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div
                  className="text-sa-success"
                  style={{ width: fallbackSize, height: fallbackSize }}
                >
                  <InflowIcon />
                </div>
              )}
              {/* Observable badges from schema ui_hint */}
              {data.observableBadges && data.observableBadges.length > 0 && (
                <ObservableBadgesContainer
                  badges={data.observableBadges}
                  position="top-right"
                  size="sm"
                />
              )}
            </div>
            {data.showLabel !== false && data.label.trim().length > 0 && (
              <div className="text-base text-sa-text text-center leading-tight max-w-[240px] break-words whitespace-normal font-medium">
                {data.label}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  const handleClass = "!bg-emerald-400 !border-sa-success !w-2.5 !h-2.5";

  return (
    <div
      data-testid={`value-flow-node-source-${id}`}
      className={`
          px-3 py-2 rounded-lg border-2 min-w-[150px]
          bg-gradient-to-br from-sa-success/10 to-sa-success/10
          ${selected ? "border-sa-success shadow-lg shadow-emerald-500/30" : "border-sa-success/40"}
          transition-all duration-200
        `}
    >
      {/* All 4 target handles */}
      <Handle type="target" position={Position.Top} id="top" className={handleClass} />
      <Handle type="target" position={Position.Right} id="right-in" className={handleClass} />
      <Handle type="target" position={Position.Bottom} id="bottom-in" className={handleClass} />
      <Handle type="target" position={Position.Left} id="left-in" className={handleClass} />
      {/* All 4 source handles */}
      <Handle type="source" position={Position.Top} id="top-out" className={handleClass} />
      <Handle type="source" position={Position.Right} id="right" className={handleClass} />
      <Handle type="source" position={Position.Bottom} id="bottom" className={handleClass} />
      <Handle type="source" position={Position.Left} id="left" className={handleClass} />

      <div className="flex items-center gap-2">
        {/* Icon */}
        <div className="relative flex-shrink-0 w-8 h-8 rounded bg-sa-success/10 flex items-center justify-center text-sa-success">
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="w-5 h-5 object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <InflowIcon />
          )}
          {/* Observable badges from schema ui_hint */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>

        {/* Label */}
        {data.showLabel !== false && (
          <div className="flex-1 min-w-0">
            <div className="text-base font-medium text-sa-success leading-tight break-words whitespace-normal">
              {data.label}
            </div>
            {data.description && (
              <div className="text-sm text-sa-success/70 leading-tight break-words whitespace-normal">
                {data.description}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

SourceNodeRF.displayName = "SourceNodeRF";
